/**
 * PrivacyPage - Privacy Policy
 */

import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PublicLayout, Container } from '../../components/layout';

export const PrivacyPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <PublicLayout>
      <Container className="py-16">
        <div className="max-w-3xl mx-auto">
          {/* Back button */}
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="mb-8 flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>

          <h1 className="text-4xl font-bold text-gray-900 mb-8">Privacy Policy</h1>

          <div className="prose prose-lg max-w-none">
            <p className="text-gray-600 mb-6">
              Last updated: February 2026
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">1. Information We Collect</h2>
            <p className="text-gray-600 mb-4">
              We collect information you provide when you create an account, make a booking, or communicate through our platform:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mb-4">
              <li><strong>Account information:</strong> Name, email address, phone number, profile photo</li>
              <li><strong>Payment information:</strong> Payment method details (processed securely by our payment partners)</li>
              <li><strong>Booking information:</strong> Dates, property details, guest count, special requests</li>
              <li><strong>Communications:</strong> Messages exchanged between guests and hosts</li>
              <li><strong>Preferences:</strong> Travel style, preferred destinations, and saved listings</li>
            </ul>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">2. How We Use Your Information</h2>
            <p className="text-gray-600 mb-4">
              We use your information to:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mb-4">
              <li>Provide, maintain, and improve our services</li>
              <li>Process bookings and payments</li>
              <li>Personalize your experience and recommendations</li>
              <li>Communicate with you about your account, bookings, and updates</li>
              <li>Ensure the safety and security of our platform</li>
              <li>Comply with legal obligations</li>
            </ul>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">3. Information Sharing</h2>
            <p className="text-gray-600 mb-4">
              We share your information only in the following circumstances:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mb-4">
              <li><strong>With hosts/guests:</strong> To facilitate bookings (e.g., your name and contact info are shared with your host after booking)</li>
              <li><strong>Service providers:</strong> Payment processors, email services, and analytics tools that help us operate</li>
              <li><strong>Legal requirements:</strong> When required by law or to protect our rights and safety</li>
            </ul>
            <p className="text-gray-600 mb-4">
              We do not sell your personal information to third parties.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">4. Data Security</h2>
            <p className="text-gray-600 mb-4">
              We implement industry-standard security measures to protect your data, including encryption in transit and at rest, secure authentication, and regular security audits. However, no method of transmission over the Internet is 100% secure.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">5. Cookies & Tracking</h2>
            <p className="text-gray-600 mb-4">
              We use cookies and similar technologies to remember your preferences, analyze usage patterns, and improve our services. You can manage cookie preferences through your browser settings.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">6. Your Rights</h2>
            <p className="text-gray-600 mb-4">
              You have the right to:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mb-4">
              <li>Access and download your personal data</li>
              <li>Correct inaccurate information</li>
              <li>Delete your account and associated data</li>
              <li>Opt out of marketing communications</li>
              <li>Object to certain data processing activities</li>
            </ul>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">7. Data Retention</h2>
            <p className="text-gray-600 mb-4">
              We retain your data for as long as your account is active or as needed to provide services. After account deletion, we may retain certain data for legal compliance, dispute resolution, or fraud prevention for a limited period.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">8. Children's Privacy</h2>
            <p className="text-gray-600 mb-4">
              DARAK is not intended for users under 18 years of age. We do not knowingly collect personal information from children.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">9. Changes to This Policy</h2>
            <p className="text-gray-600 mb-4">
              We may update this Privacy Policy from time to time. We will notify you of significant changes via email or a notice on our platform.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">10. Contact Us</h2>
            <p className="text-gray-600 mb-4">
              For privacy-related questions or requests, contact us at{' '}
              <a href="mailto:hello@kodefoundry.com" className="text-primary-600 hover:underline">hello@kodefoundry.com</a>
            </p>
          </div>

          {/* Related links */}
          <div className="mt-12 pt-8 border-t border-gray-200">
            <p className="text-sm text-gray-500">
              See also:{' '}
              <Link to="/terms" className="text-primary-600 hover:underline">Terms of Service</Link>
              {' | '}
              <Link to="/host-terms" className="text-primary-600 hover:underline">Host Terms</Link>
              {' | '}
              <Link to="/host-guidelines" className="text-primary-600 hover:underline">Community Guidelines</Link>
            </p>
          </div>
        </div>
      </Container>
    </PublicLayout>
  );
};

export default PrivacyPage;
